<!--
  Route-fork PR-R1: StorefrontLayout.

  Extracts the shopper-facing chrome that App.vue conditionally rendered
  via `v-if="isLoggedIn && isShopper"`. Brand color comes from
  `$store.getters.getBrandColor` (existing storefront theming). Cart icon
  with item count, logout button. PR-R2 will harmonize the storefront
  cart popup mechanics with the rest of the layout.

  Issue #2178: this is now the ONE full-width brand banner. The store name
  (getStoreName) sits on the LEFT; Cart/Logout on the right.

  TWO SEPARATE CONCERNS live in this banner, and they are gated separately:

    1. Brand IDENTITY -- the brand-color bar, the logo, the store name.
       This is PUBLIC. It renders for EVERY storefront visitor: signed-out
       browsers, signed-in non-shoppers (admin/distributor/owner), real
       shoppers, and the editor preview alike. A store's identity is what
       the public storefront is FOR; hiding it behind a session made the
       page look unbranded to everyone who was not already a shopper.

    2. Interactive CONTROLS. These are NOT one group, and treating them as
       one was a bug in two directions at once. They split by the question
       each actually answers:

         a. The CART -- "can this visitor shop?" -- answer: EVERYONE. It is
            ungated: no login check, no role check. Browsing and adding to a
            cart are anonymous, and identity is claimed at checkout.

         b. PROFILE / ORDERS / ADDRESSES / LOGOUT -- "is there a real,
            personal account behind this session?" -- `hasPersonalSession`.
            These lead to personal data, and the storefront swaps every
            anonymous visitor into ONE shared shopper account per store, so
            offering them to a visitor exposes the previous visitor's orders
            and saved addresses.

       Neither consults ROLE. Any identity may shop: an owner, a client
       admin, a Super Admin, and an SA impersonating a shopper all get the
       same set as a signed-in shopper. See hasPersonalSession below.

  These two were previously fused into a single v-if on the <header>
  (`isShopperSession || isStorefrontPreview($route)`), which meant the brand
  color, logo and name all disappeared for any non-shopper. That was the
  bug: the gate that correctly protected the controls was also, silently,
  suppressing the branding.

  Consequence worth keeping in mind when editing: anything OUTSIDE the
  `hasPersonalSession` template is public and will be seen by anonymous
  visitors. Anything that reads or links to PERSONAL data belongs INSIDE it.
  When adding a control, decide which of the two questions above it answers
  -- "can they shop?" or "do they have an account?" -- and place it
  accordingly. Defaulting to the wrong one is how this surface broke before.

  Preview (?preview=1) needs no special case here any more. A preview viewer
  establishes no session, so it lands on the no-personal-session branch by
  the general rule rather than its own: branding and the cart, no personal
  links. The cart being visible in preview is cosmetic rather than
  functional -- the editor stacks a transparent interaction-blocking overlay
  above the iframe (plus pointer-events:none on the iframe itself), so the
  button cannot be clicked there. Reading getStoreName/getBrandColor is a
  plain read; nothing here mutates session state.

  Store logo overlay (Trello uGbbyBlq): the brand LOGO lands here, in the
  left slot reserved above, ahead of the store name. Sourced from
  getBrandLogoUrl (committed by useStorefrontChrome, resolved from the
  store's `brand_image` column -- the SAME upload path the store editor's
  Branding tab already writes via ImageUploader/shared /api/upload_image).
  A store with no logo uploaded has getBrandLogoUrl === null, and the v-if
  keeps the <img> out of the DOM entirely -- no broken-image icon, no empty
  box, byte-for-byte the same banner as before this change.
-->
<template>
    <div class="storefront-layout">
        <!--
          HEADER INVERSION (T+L demo, Jake's call). This bar is WHITE, not the
          brand color; the brand color moved down to StorefrontCategoryNav.

          Why: a distributor's uploaded logo is routinely an opaque JPEG with
          white baked into its pixels and no alpha channel -- T+L's real logo
          is exactly that (CMYK, 2198x514, ink running edge to edge, zero
          white margin on any side). Against a brand-colored bar it rendered
          as a hard white block butting into the teal, and no CSS can make a
          JPEG's white transparent. Making the bar white turns that baked-in
          white into the correct background instead of a defect.

          The background is bound INLINE (rather than left to the stylesheet)
          so that this element's own style attribute is the single, greppable
          answer to "what color is this bar" -- it is where the brand color
          used to be, and the place anyone debugging the inversion will look.
        -->
        <header
            class="storefront-topbar"
            :style="{ background: topbarBackground }"
            data-testid="storefront-topbar"
        >
            <!--
              The logo navigates to the store Home, like the store name and
              the category nav's Home item -- all three read the SAME `homeTo`
              so they cannot drift apart about where Home is.

              The data-testid stays on the IMG rather than moving to the
              anchor: existing selectors (storefront-layout.spec.js, the
              dm-store-logo-overlay ui-scenario) match on it, and the no-logo
              guard is expressed as `v-if="brandLogoUrl"` on this element.
              The v-if sits on the IMG for the same reason, so a store with no
              logo renders neither an image nor an empty anchor -- an empty
              anchor would be an invisible click target in the bar.

              When no slug resolves there is no Home to link to, so the image
              renders unwrapped rather than inside a link to null (same
              null-handling as the store name directly below).

              Preview-safe by the same mechanism as the name and the nav: the
              editor's transparent overlay + pointer-events:none on the iframe
              swallow the click. No preview-only @click is added here.
            -->
            <!--
              LEFT SLOT. Always present, even with no logo: the grid's three
              columns are positional, so an absent first child would promote
              the store name into the left track and un-centre it. An empty
              div has no box of its own and is invisible.
            -->
            <div class="storefront-topbar__brand">
            <router-link
                v-if="brandLogoUrl && homeTo"
                :to="homeTo"
                class="storefront-topbar__logo-link"
            >
                <img
                    :src="brandLogoUrl"
                    :alt="`${storeName} home`"
                    class="storefront-topbar__logo"
                    data-testid="storefront-topbar-logo"
                />
            </router-link>
            <img
                v-else-if="brandLogoUrl"
                :src="brandLogoUrl"
                :alt="storeName"
                class="storefront-topbar__logo"
                data-testid="storefront-topbar-logo"
            />
            </div>

            <!--
              The store name links to the storefront Home (/shop/<slug>) --
              the SAME target StorefrontCategoryNav's Home item navigates to
              (both resolve the slug via resolveStorefrontSlug), so Home from
              the name === Home from the nav. It is a real router-link (link
              a11y + keyboard-activatable) and keeps the data-testid.

              Preview-safe by the same mechanism as the nav: in the editor
              preview iframe (?preview=1) the router-links here are inert
              because StoreEditShell stacks a transparent interaction-blocking
              overlay above the iframe (plus pointer-events:none on the iframe
              itself) -- the click never reaches this link. We add NO
              preview-only @click that could escape the iframe. When no slug
              can be resolved there is no Home to link to, so the name falls
              back to a non-navigating span (never a broken /shop/ link).
            -->
            <router-link
                v-if="homeTo"
                :to="homeTo"
                class="storefront-topbar__store-name storefront-topbar__store-name--link"
                data-testid="storefront-topbar-store-name"
                >{{ storeName }}</router-link
            >
            <span
                v-else
                class="storefront-topbar__store-name"
                data-testid="storefront-topbar-store-name"
                >{{ storeName }}</span
            >

            <!--
              RIGHT SLOT: the third and last grid column.

              The wrapper exists so the bar has exactly THREE children and the
              three-column mapping is positional and unambiguous. As loose
              children the controls each consumed a track, so the store name's
              column depended on how many controls the session happened to
              render -- which is the shape of the bug this layout fixes.

              Adding a control here is now safe by construction: it joins this
              flex row and widens the track, and the name's centre cell
              yields to it automatically. Nothing needs re-measuring.
            -->
            <div class="storefront-topbar__controls" data-testid="storefront-topbar-controls">

            <!--
              SEARCH: immediately BEFORE the cart (Jake, 2026-08-29: "Next to
              the Cart button I want a magnifying glass icon that expands to
              a search field").

              UNGATED for the same reason the cart is: searching is part of
              browsing, and browsing is anonymous. It sits outside the
              hasPersonalSession block deliberately -- a signed-out visitor
              is exactly the shopper most likely to be looking for something.

              Rendered only when a store slug resolves: every search path is
              slug-scoped (/shop/<slug>/search), so without one there is no
              store to search and the control would be inert. Same guard
              shape as accountBase/homeTo above.
            -->
            <storefront-search-box v-if="searchSlug" :store-slug="searchSlug" />

            <!--
              CART: no gate at all. Anyone can shop -- signed in or not, any
              role. Browsing and adding to a cart are anonymous; identity is
              claimed at checkout. See hasPersonalSession below for the line
              that IS drawn, and why it is a different question.
            -->
            <button
                class="storefront-topbar__btn"
                data-testid="storefront-cart-icon"
                @click="cartClick"
            >
                <!-- Testid on the COUNT itself, not just the button: the
                     button is always present, so only the count can tell a
                     populated cart from an empty one. -->
                <!-- Counts only the store the page shows, and renders only
                     once that count is known: the one number the browser
                     has before then is the WHOLE account cart's, which
                     counts other stores' items (see cartItems). -->
                <span
                    v-if="cartItems !== null"
                    class="storefront-badge"
                    data-testid="storefront-cart-count"
                >{{ cartItems }}</span>
                <span>Cart</span>
            </button>

            <!--
              HELP: deliberately OUTSIDE the hasPersonalSession block. Help
              is public (`docs/user/HELP-SYSTEM-DESIGN.md` decision 4) and
              the shopper most likely to need it is the one who has not
              signed in yet -- putting this entry point behind a session
              would hide it from exactly that reader. Not slug-scoped: help
              is the same content for every store.

              CONTEXT-AWARE: the target is computed from the current route
              (`helpContextMap.js`). Unmapped routes fall back to the
              shopper help index, so this is never a dead link.

              OPENS THE FLYOVER, does not navigate (Jake's review: help
              "should be a flyover like the store preview, not a dedicated
              page"). A shopper mid-purchase should not lose the page they
              are on to read how to finish it. `helpTo` is unchanged and
              still contextual -- it is now the flyover's iframe target
              rather than a router destination, and `/help/shopper/*`
              remains a real route for direct links.

              ROUND ICON BUTTON, not the green pill it used to be (Jake,
              2026-08-28: "the Help button should be a round button with a ?
              inside a circle since the rectangular button is distracting").

              TWO things changed and only one of them was literally asked
              for. The shape is the request. Dropping the SOLID GREEN FILL
              is a judgement call made with it, because the fill was the
              distracting part: the pill was filled in the same green as the
              category band immediately below, so it read as a second,
              louder navigation element sitting in the quiet white bar. A
              solid green DISC is no less loud than a solid green rectangle,
              so rounding the corners alone would have answered the letter
              of the request and missed it. The outlined treatment below
              keeps Help a standing, findable affordance without competing
              with the store name.

              THE LABEL IS GONE, WHICH MAKES `aria-label` LOAD-BEARING.
              With the word "Help" removed the button has no other source of
              an accessible name -- the ligature span is aria-hidden and
              contributes nothing -- so a screen reader would announce a
              bare "button" without it. `title` carries the same word for a
              sighted mouse user, who has likewise lost the label.

              ADJACENCY TO PROFILE IS GONE, DELIBERATELY. Help used to sit
              immediately before the profile link, and that adjacency was
              pinned by `help-topbar-affordance.spec.js`. Profile now lives
              inside the account menu, so there is no longer a top-level
              profile control to be adjacent TO. What that assertion was
              really protecting -- Help being present, public, and next to
              the personal-account affordance rather than buried -- is
              preserved: Help is the last public control and sits
              immediately before the account trigger. The spec was updated
              to assert that relationship instead of the stale one.
            -->
            <button
                type="button"
                class="storefront-topbar__help"
                data-testid="storefront-topbar-help"
                aria-label="Help"
                title="Help"
                :aria-expanded="helpFlyoverOpen ? 'true' : 'false'"
                aria-haspopup="dialog"
                @click="openHelpFlyover"
            >
                <span class="tf-icon" aria-hidden="true">help_outline</span>
            </button>

            <!--
              ACCOUNT MENU (Jake, 2026-08-28: "Profile, Orders, Addresses, and
              Logout all need to be under a menu in the top-right corner that
              looks like a little person silhouette").

              ONE trigger replaces FOUR labelled controls. That is a UX request
              and a geometry fix at the same time, and the second half is worth
              spelling out because it is the reason this is not merely tidier.
              The store name is the centre cell of a `1fr auto 1fr` grid, so the
              space it gets is whatever the two side tracks leave. Four text
              controls ("Profile", "Orders", "Addresses", "Logout") measured
              ~300px of right track; one 40px avatar does not. Issue #3056 is
              the name colliding with the LOGO and the cart badge at ~780px --
              a symptom of a right track too wide for the bar -- so narrowing
              that track is the direct relief. See the PR for measured numbers.

              THE GATE IS UNCHANGED AND STILL TWO-LEVEL, deliberately:

                * the whole menu sits inside `hasPersonalSession`, so an
                  anonymous visitor riding the store's SHARED auto-provisioned
                  shopper account is offered nothing -- exactly as before.
                  Collapsing controls must not become a way to smuggle personal
                  links back to a session that must not see them.

                * INSIDE the menu, Profile / Orders / Addresses keep their own
                  `v-if="accountBase"`, while Logout does not. This asymmetry
                  is inherited from the previous bar and is kept on purpose: the
                  three links are slug-scoped routes that cannot be BUILT
                  without a slug, whereas logging out is slug-independent and is
                  the one action a signed-in person must never be denied. A
                  slug-less signed-in session therefore still gets the avatar,
                  containing only Logout. Hiding the trigger entirely in that
                  case would look tidier and would strand the session with no
                  way to sign out.

              Vuetify `v-menu` with an activator slot is the idiom already used
              for this pattern in this codebase (PortalScopePill.vue's scope
              switcher, VariantMenu.vue) -- matched rather than reinvented, so
              keyboard behaviour, focus return and dismissal come from the same
              component the rest of the app is tested against.

              ACCESSIBILITY. The trigger is icon-only, so its accessible name
              comes entirely from `aria-label` -- the ligature text inside the
              glyph span is `aria-hidden` and contributes nothing. `title`
              carries the same word for a sighted mouse user, and
              `aria-haspopup="menu"` plus `aria-expanded` tell assistive tech
              that this opens a menu rather than navigating. The four controls
              inside keep the `aria-label`/`title` they carried in the bar.
            -->
            <template v-if="hasPersonalSession">
            <v-menu
                v-model="accountMenuOpen"
                location="bottom end"
                :close-on-content-click="true"
            >
                <template #activator="{ props: menuProps }">
                    <button
                        type="button"
                        class="storefront-topbar__account"
                        data-testid="storefront-topbar-account-menu"
                        aria-label="Account menu"
                        title="Account"
                        aria-haspopup="menu"
                        :aria-expanded="accountMenuOpen ? 'true' : 'false'"
                        v-bind="menuProps"
                    >
                        <v-icon
                            icon="account_circle"
                            size="26"
                            aria-hidden="true"
                        />
                    </button>
                </template>

                <!--
                  Every item keeps the data-testid it had as a top-level
                  control, ON THE SAME KIND OF INTERACTIVE ELEMENT: the three
                  navigations stay router-links, Logout stays a button. That is
                  the contract eight ui-scenarios and five specs address. A
                  testid parked on the surrounding v-list-item would still
                  "exist" while no longer being the thing that navigates, which
                  would turn every scenario that clicks it into a silent no-op.
                -->
                <v-list
                    density="compact"
                    class="storefront-account-menu"
                    data-testid="storefront-topbar-account-list"
                >
                    <template v-if="accountBase">
                        <v-list-item>
                            <router-link
                                :to="`${accountBase}/profile`"
                                class="storefront-account-menu__item"
                                data-testid="storefront-topbar-profile"
                                title="Profile"
                                aria-label="Profile"
                            >
                                Profile
                            </router-link>
                        </v-list-item>
                        <v-list-item>
                            <router-link
                                :to="`${accountBase}/orders`"
                                class="storefront-account-menu__item"
                                data-testid="storefront-topbar-orders"
                            >
                                Orders
                            </router-link>
                        </v-list-item>
                        <v-list-item>
                            <router-link
                                :to="`${accountBase}/addresses`"
                                class="storefront-account-menu__item"
                                data-testid="storefront-topbar-addresses"
                            >
                                Addresses
                            </router-link>
                        </v-list-item>
                    </template>

                    <v-list-item>
                        <button
                            type="button"
                            class="storefront-account-menu__item"
                            data-testid="storefront-logout"
                            @click="logout"
                        >
                            Logout
                        </button>
                    </v-list-item>
                </v-list>
            </v-menu>
            </template>

            </div>
        </header>

        <main
            class="storefront-main"
            data-testid="storefront-scroll-region"
            :style="{ background: $store.getters.getBackgroundColor }"
        >
            <!--
              THE CATEGORY NAV, rendered ONCE for every storefront page (Jake,
              2026-10-19: "The nav should exist on every page in the store").
              It used to be rendered by four pages, so cart, orders, profile
              and the rest had the header but no nav.

              It is the FIRST child of the scroll region, ahead of the page,
              which is what lets `position: sticky; top: 0` pin it to the
              header's bottom edge (see StorefrontCategoryNav).

              GATED (security/privacy). The nav lists category names, so it
              renders only when `navSlug` is set: the session gate has
              admitted the visitor to this store WITH a session. That is the
              state the pages beneath it render from, so the nav can never be
              more permissive than the page. The password wall, the loading /
              closed / not-found / unavailable screens and the sessionless
              order-link pages never set it. The /nav read is itself refused
              (403) for a password store without a pass; a refusal hides the
              nav (see showNav). Preview (?preview=1) is admitted by the same
              gate, so the editor iframe gets the nav too.
            -->
            <storefront-category-nav
                v-if="showNav"
                :nav="nav"
                :store-slug="navSlug"
                :active-slug="navActiveSlug"
            />
            <slot />
        </main>

        <!--
          Help flyover. Mounted at the LAYOUT level so it floats over every
          storefront screen and survives navigation between them; it
          re-resolves its contextual article on each route change rather
          than being remounted per page.
        -->
        <help-flyover
            audience="shopper"
            :open="helpFlyoverOpen"
            @close="closeHelpFlyover"
        />
    </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import { resolveStorefrontSlug } from "@/router/storefrontSlug";
import { logout as serverLogout } from "@/services/accountService";
// Distinguishes the store's SHARED auto-provisioned shopper session from a
// real personal one. Nothing else can: after the swap the shared session has
// a real token and roleName "Shopper". See hasPersonalSession below.
import { isAnonymousStorefrontSession } from "@/router/anonymousStorefrontSession";
import { resolveHelpTarget } from "@/router/helpContextMap";
import HelpFlyover from "@/components/Help/HelpFlyover.vue";
import { helpFlyoverState, closeHelpFlyover } from "@/components/Help/useHelpFlyover";
import StorefrontSearchBox from "@/components/Storefront/StorefrontSearchBox.vue";
import { fetchStoreCartCount } from "@/components/Storefront/storeCartCount";
import { resolveCartAccountId } from "@/router/cartAccount";
import { gateStoreFor, gateAdmitsContentFor } from "@/router/storefrontStoreId";
import StorefrontCategoryNav from "@/components/Storefront/StorefrontCategoryNav.vue";
import { useStorefrontNav } from "@/composables/useStorefrontNav";

// Passed as the nav's `activeSlug` on pages that are neither Home nor a
// category (cart, orders, search, ...) so NO entry is highlighted: an empty
// slug would light up Home, which would misreport where the shopper is. Not a
// valid store category slug (those are lowercase alphanumeric + hyphen), so it
// can never match a real one.
const NO_ACTIVE_CATEGORY = "__none__";

export default {
    name: "StorefrontLayout",
    components: {
        HelpFlyover,
        "storefront-search-box": StorefrontSearchBox,
        "storefront-category-nav": StorefrontCategoryNav,
    },
    setup() {
        const { nav, error: navError, fetchNav } = useStorefrontNav();
        return { nav, navError, fetchNav };
    },
    data() {
        return {
            // Help flyover open state. Owned by the layout because the
            // flyover floats over every storefront screen and must outlive
            // navigation between them.
            helpFlyoverOpen: false,
            sharedHelp: helpFlyoverState,
            /**
             * Account-menu open state.
             *
             * Bound with v-model purely so the trigger can publish a TRUTHFUL
             * `aria-expanded`. Vuetify's activator props do not provide one --
             * grepped the shipped library: VCombobox is the only component that
             * sets `aria-expanded` at all -- so a button declaring
             * `aria-haspopup="menu"` without this announces that it opens a
             * menu and then never says whether the menu is open. For a
             * screen-reader user that is the difference between "I pressed it,
             * did anything happen?" and a usable control.
             *
             * The menu still OWNS its own open/close behaviour; this only
             * mirrors it. Nothing here opens or closes the menu imperatively.
             */
            accountMenuOpen: false,
            // The header badge's count and the store id it was counted for.
            // Only shown while `storeCartCountFor` is the CURRENT store, so
            // another store's answer can never be displayed here. See
            // cartItems and refreshStoreCartCount.
            storeCartCount: null,
            storeCartCountFor: null,
            // Bumped by every refresh; only the newest may write the count.
            storeCartCountSeq: 0,
            // The store whose nav `nav` currently holds, or null. Set only
            // after a successful read FOR that store, so a slug change (or a
            // refused read) can never leave another store's categories up.
            navLoadedFor: null,
        };
    },
    beforeUnmount() {
        // The shared (?) state is module-level and outlives this layout.
        // Leaving it open/targeted would stop the next layout's watcher from
        // firing and leak a stale target into its plain Help button.
        closeHelpFlyover();
    },
    watch: {
        // A (?) help icon on the page asked for the flyover; the layout
        // still owns the panel's open state (see useHelpFlyover.js).
        "sharedHelp.open"(isOpen) {
            if (isOpen) this.helpFlyoverOpen = true;
        },
        navSlug: {
            handler(slug) {
                this.loadNav(slug);
            },
            immediate: true,
        },
        // Re-count whenever the store, the account, or the cart's lines
        // change (an add, a quantity step, the cart page committing rows).
        storeCartCountKey: {
            handler() {
                this.refreshStoreCartCount();
            },
            immediate: true,
        },
    },
    computed: {
        /**
         * Is there a REAL, PERSONAL account behind this session?
         *
         * Gates the personal-account links (Profile / Orders / Addresses) and
         * Logout. NOT the cart -- the cart is ungated, because "can this
         * visitor shop?" is a different question with a different answer
         * (everyone). Keeping them separate is the fix; fusing them into one
         * condition is what produced the bug.
         *
         * TWO PARTS, both necessary:
         *
         *   isLoggedIn -- there is a session at all.
         *
         *   !isAnonymousStorefrontSession() -- and it is not the store's
         *   SHARED auto-provisioned shopper. This is the load-bearing half.
         *   The storefront swaps every anonymous visitor into one shared
         *   account per store, which leaves isLoggedIn true, isShopper true
         *   and roleName "Shopper" -- so isLoggedIn ALONE cannot tell an
         *   anonymous visitor from a person, and the old gate showed them
         *   Orders and Addresses belonging to whoever visited before them.
         *   See router/anonymousStorefrontSession.js.
         *
         * ROLE IS DELIBERATELY NOT CONSULTED. Any identity may shop and keep
         * their own account links: an owner, a client admin, a Super Admin,
         * and an SA impersonating a shopper are all real personal sessions.
         * The old `isShopper` check read the RAW role, so an impersonating SA
         * (role.name "Super Admin") was denied even a cart. Dropping the role
         * check makes that bug disappear rather than adding an
         * impersonation-aware branch to compensate for it -- one mechanism
         * per concept (docs/design-philosophy.md), and nothing here to drift
         * out of sync with the impersonation model.
         *
         * Preview (?preview=1) needs no branch: the editor preview does not
         * establish a session, so it lands on false and gets branding without
         * controls, exactly as before.
         */
        hasPersonalSession() {
            return (
                this.$store.getters.isLoggedIn &&
                !isAnonymousStorefrontSession()
            );
        },
        /**
         * Contextual Help target: the article about the screen the shopper is
         * on, or the shopper help index when this route has no article. See
         * router/helpContextMap.js.
         *
         * NOT slug-scoped, matching the previous hardcoded `/help/shopper`:
         * help is the same team-authored content for every store, and the
         * shopper help routes are deliberately outside the `/shop/:slug/*`
         * session tree (see ShopperHelpPage.vue).
         */
        helpTo() {
            return resolveHelpTarget(this.$route, "shopper");
        },
        /**
         * The brand bar's background -- WHITE for every store, by design.
         *
         * This is deliberately a constant rather than a read of
         * getBrandColor. The brand color has not stopped mattering; it moved
         * to StorefrontCategoryNav (see the header comment above for the
         * opaque-logo reason). Keeping it as a named computed rather than a
         * literal in the template documents that the whiteness is a decision
         * about logo legibility, not an oversight or a lost binding -- the
         * next reader who sees a white bar on a branded store finds the
         * answer here instead of assuming the getter broke.
         */
        topbarBackground() {
            return "#ffffff";
        },
        // Store name for the banner (issue #2178). Sourced from the Vuex
        // store, populated by StorefrontViewShopper the same way brandColor
        // is (setStoreName / getStoreName). Falls back to "Shop" when the
        // name is empty/unknown, preserving the prior banner behavior.
        storeName() {
            return this.$store.getters.getStoreName || "Shop";
        },
        // Resolved brand logo URL (Trello uGbbyBlq), or falsy when the
        // store has no logo uploaded -- the template's v-if keeps the
        // <img> element out of the DOM entirely in that case (no broken
        // image, no empty box).
        brandLogoUrl() {
            return this.$store.getters.getBrandLogoUrl || null;
        },
        // Router target for the store-name Home link. Resolves the slug the
        // SAME way StorefrontCategoryNav's Home item does (resolveStorefrontSlug
        // -> /shop/<slug>), so clicking the name goes to the identical Home
        // page as the nav's Home entry. Null when no slug resolves -- the
        // template then renders a non-navigating span instead of a broken link.
        homeTo() {
            const slug = resolveStorefrontSlug(this.$route);
            return slug ? `/shop/${slug}` : null;
        },
        /**
         * Base path for the shopper's account links (Profile / Orders /
         * Addresses), card https://trello.com/c/BxskXBad.
         *
         * Null when no slug resolves -- the account routes are all
         * slug-scoped, so without one there is no valid link to render.
         * Returning null (rather than a bare `/shop//profile`) keeps the
         * links out of the DOM entirely instead of shipping broken ones.
         *
         * This is the SAME slug-scoped base as `homeTo`, so it is an alias
         * rather than a second copy of the resolution -- one place to change
         * if the storefront base path ever moves, and no way for the two to
         * drift into disagreeing about where this store lives.
         */
        accountBase() {
            return this.homeTo;
        },
        /**
         * The store slug the search box searches, or null when none
         * resolves (the template then omits the control entirely).
         *
         * Derived from the SAME resolveStorefrontSlug call as homeTo rather
         * than re-reading the route, so the search box can never end up
         * pointed at a different store than the rest of the chrome.
         */
        searchSlug() {
            return resolveStorefrontSlug(this.$route) || null;
        },
        /**
         * The store the header badge counts: the id the session gate resolved
         * from this URL's slug and published (router/storefrontStoreId.js) --
         * the SAME id the pages under the gate use. Null outside the gate (a
         * help article in this layout) and while the gate is still entering.
         */
        /**
         * The store whose nav may be shown, or null. Non-null only while the
         * session gate has admitted the visitor to the store in the URL with a
         * session (router/storefrontStoreId.js) -- see the template comment.
         */
        navSlug() {
            const slug = resolveStorefrontSlug(this.$route) || null;
            return slug && gateAdmitsContentFor(slug) ? slug : null;
        },
        showNav() {
            return (
                this.navSlug !== null &&
                this.navLoadedFor === this.navSlug &&
                Array.isArray(this.nav) &&
                this.nav.length > 0
            );
        },
        /**
         * Which nav entry is highlighted: Home on the store home, the page's
         * category on a category / product page, nothing anywhere else.
         */
        navActiveSlug() {
            switch (this.$route?.name) {
                case "storefront_home":
                    return "";
                case "storefront_category":
                case "storefront_product":
                    return this.$route.params.categorySlug || "";
                default:
                    return NO_ACTIVE_CATEGORY;
            }
        },
        cartStoreId() {
            return gateStoreFor(this.searchSlug);
        },
        /**
         * The header badge: units of the cart that belong to the store the
         * page shows, or null while that is not known (the badge then shows
         * no number).
         *
         * NOT a local sum. Carts are keyed (account, store), and `state.cart`
         * is ONE store's cart -- whichever page last read or wrote it, which
         * need not be this store's yet. Summing a local cart once put store
         * A's items on store B's badge (Mike on stage, 2026-10-07). The count
         * comes from the server for the store the page shows.
         */
        cartItems() {
            const storeId = this.cartStoreId;
            if (storeId == null || this.storeCartCountFor !== storeId) return null;
            return this.storeCartCount;
        },
        /**
         * What the badge's count depends on: the store, whether there is an
         * account, and the local cart's lines (product + quantity). The lines
         * are only a CHANGE SIGNAL (an add, a quantity step, a checkout),
         * never the answer: `state.cart` may be another store's. A string, so
         * the watcher fires on a real change only.
         */
        storeCartCountKey() {
            const account = this.$store?.state?.account;
            const cart = this.$store?.state?.cart;
            const lines = Array.isArray(cart)
                ? cart.map((item) => [
                      item?.campaign_product_id ?? null,
                      item?.quantity ?? 0,
                  ])
                : null;
            return JSON.stringify([this.cartStoreId, !!account, lines]);
        },
    },
    methods: {
        /**
         * Read the nav for an admitted store. `slug` is null whenever the
         * visitor is not admitted, in which case nothing is fetched at all.
         * A refused or failed read leaves `navLoadedFor` null, so the bar
         * stays hidden.
         */
        async loadNav(slug) {
            this.navLoadedFor = null;
            if (!slug) return;
            await this.fetchNav(slug);
            if (this.navSlug === slug && !this.navError) {
                this.navLoadedFor = slug;
            }
        },
        /**
         * Load the badge's count for the store the page shows.
         *
         * No store (outside the gate, or entry still running) -> no number
         * and no request. No account -> 0: there is no cart (logged out).
         * Otherwise the server answers (`store_cart_count`) -- ALWAYS: the
         * local cart may be another store's. A failed answer for a NEW store
         * leaves no number; for the SAME store it keeps the last count,
         * which was this store's.
         */
        async refreshStoreCartCount() {
            const seq = this.storeCartCountSeq + 1;
            this.storeCartCountSeq = seq;
            const storeId = this.cartStoreId;
            if (storeId == null) {
                this.storeCartCount = null;
                this.storeCartCountFor = null;
                return;
            }
            if (!this.$store?.state?.account) {
                this.storeCartCount = 0;
                this.storeCartCountFor = storeId;
                return;
            }
            const accountId = resolveCartAccountId({ vuexStore: this.$store });
            if (accountId == null || !this.$axios) {
                this.storeCartCount = null;
                this.storeCartCountFor = null;
                return;
            }
            try {
                const count = await fetchStoreCartCount(
                    this.$axios,
                    accountId,
                    storeId
                );
                if (seq !== this.storeCartCountSeq) return;
                this.storeCartCount = count;
                this.storeCartCountFor = storeId;
            } catch (err) {
                if (seq !== this.storeCartCountSeq) return;
                if (this.storeCartCountFor !== storeId) {
                    this.storeCartCount = null;
                    this.storeCartCountFor = null;
                }
                // eslint-disable-next-line no-console
                console.warn("Cart badge: could not count this store's items", describeError(err));
            }
        },
        openHelpFlyover() {
            this.helpFlyoverOpen = true;
        },
        closeHelpFlyover() {
            this.helpFlyoverOpen = false;
        },
        async logout() {
            // Revoke the token on the server first -- the local clear below
            // removes the token this request authenticates with. Best-effort:
            // a failure here must never stop the local logout.
            try {
                await serverLogout();
            } catch (e) {
                console.error(describeError(e));
            }
            try {
                await this.$store.dispatch("logout");
                // A shopper is NEVER sent to /login: back to the store home.
                const slug = resolveStorefrontSlug(this.$route);
                this.$router.push(slug ? `/shop/${slug}` : "/");
            } catch (e) {
                console.error(describeError(e));
            }
        },
        cartClick() {
            // Bug #1848: bare /cart only worked when localStorage.storeUrl was
            // set (legacy /store/:id entry path). Shoppers who arrive via a
            // slug route carry the slug in route.params.slug and have no
            // storeUrl in localStorage, so /cart bounced them to /login. Use
            // resolveStorefrontSlug to build the canonical path.
            // Convergence (card q7EQ3a0b): cart moved to /shop/:slug/cart.
            const slug = resolveStorefrontSlug(this.$route);
            this.$router.push(slug ? `/shop/${slug}/cart` : "/login");
        },
    },
};
</script>

<style scoped>
.storefront-layout {
    /* Fixed-viewport shell. See DistributorLayout for the full rationale.
     *
     * Height is anchored to the dynamic viewport unit (100dvh) rather than the
     * height:100% percentage chain (html/body/#app -> v-application -> v-main).
     * On iOS Safari that percentage chain does NOT reliably recompute on
     * orientationchange: after a portrait->landscape->portrait rotate the shell
     * held a stale height and `overflow: hidden` clipped the topbar out of view
     * until a refresh. 100dvh is the unit iOS recomputes on viewport/orientation
     * change, so the header stays visible across rotate cycles.
     *
     * height:100% is the fallback for engines without dvh support: they ignore
     * the unknown `100dvh` value and keep the percentage chain (prior behavior).
     *
     * MINUS THE LAYOUT'S BOTTOM RESERVATION (card mq26K91V, 2026-09-20).
     *
     * This comment used to claim 100dvh "equals the height:100% value in the
     * steady state (v-main fills the same viewport)". That premise is what
     * broke, and it is worth naming because the two units only agree while
     * NOTHING is reserved. App.vue's global version/status bar is a
     * `<v-footer app>`, and Vuetify's `app` prop registers it as a FIXED
     * layout element whose height is reserved as `padding-bottom` on
     * `.v-main` (VMain.css: `padding-bottom: var(--v-layout-bottom)`).
     * Padding shrinks v-main's CONTENT box; a 100dvh child measures the
     * VIEWPORT and does not see it. So this shell overflowed v-main's content
     * box by exactly the reserved amount and ran under the fixed bar, taking
     * the storefront footer -- the "Powered by" mark and the Privacy/Refund
     * links -- with it. Measured before the fix at 1280x720: shell 0..720,
     * bar 688..720, footer 679..720, 32px of overlap, 24 of the mark's 25px
     * invisible. Jake found it on 8083 testing white-label.
     *
     * `--v-layout-bottom` is the RIGHT quantity to subtract rather than a
     * magic 32px: Vuetify sets it inline on `.v-main` (so it inherits here),
     * and it is the sum of whatever bottom layout elements are registered.
     * It is 0px when none are -- which is the shopper case, since
     * `showVersionFooter` excludes shoppers -- so the shopper storefront
     * keeps its byte-identical full-viewport shell and this change is inert
     * there. It also tracks the bar appearing or disappearing without a
     * remount, which a hard-coded height could not.
     *
     * The `var(..., 0px)` fallback covers a render before Vuetify has set the
     * variable: falling back to 0 yields exactly the previous behavior, so
     * the worst case is the old bug for one frame rather than a shell of
     * indeterminate height.
     *
     * The height:100% fallback line needs no equivalent subtraction -- a
     * percentage of v-main's content box already excludes its padding, which
     * is why the three other layouts (Distributor/Portal/SuperAdmin) use
     * height:100% and never had this bug. */
    height: 100%;
    height: calc(100dvh - var(--v-layout-bottom, 0px));
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.storefront-topbar {
    /* THREE-COLUMN GRID: [left slot | store name | controls].
     *
     * The name is a real grid CELL, bounded by its siblings, rather than an
     * absolutely-positioned box kept clear of them by arithmetic. That
     * distinction is the whole fix -- see the store-name rule below for why
     * two previous attempts at the arithmetic could not work.
     *
     * `1fr auto 1fr` is what preserves VIEWPORT centring, and it is not the
     * same thing as flex `justify-content: center` (which the store-name
     * comment rightly warns against). Equal `1fr` side tracks are forced to
     * the SAME width regardless of how much content each holds, so the centre
     * track is symmetric about the bar's midpoint -- the name stays on the
     * viewport centre whether the logo is wide, narrow or absent, and whether
     * the shopper is signed in (six controls) or out (two). Measured drift is
     * 0px at 1280/1440/1920.
     *
     * When the bar genuinely cannot fit a centred name -- a signed-in shopper
     * at ~1150px, where the controls alone claim 559px of it -- the side
     * tracks give way and the name slides rather than colliding or vanishing.
     * That degradation is the POINT: `justify-content: center` and the old
     * absolute+max-width both had to choose between overlapping the controls
     * and erasing the name. */
    display: grid;
    /* `minmax(0, auto)` on the CENTRE track, not a bare `auto`, and that
     * distinction is the whole of the #3056 fix.
     *
     * A bare `auto` track sizes to its content's MAX-CONTENT width and refuses
     * to go below it. Measured at 700px with a long store name, the tracks
     * resolved to 176.6px | 290.8px | 176.6px: the centre track held the name's
     * full 290.8px and the `1fr` side tracks were squeezed to 176.6px each --
     * narrower than the brand slot's actual 218px. The logo therefore overflowed
     * its own track rightward to x=238 while the name's cell began at x=204.6,
     * and the two painted over each other. That is exactly the reported bug:
     * the store name on top of the logo at narrow-but-desktop widths.
     *
     * `min-width: 0` on the store name CANNOT fix this, which is why the
     * previous attempt looked correct and was not. That property lets the ITEM
     * shrink inside its track; here it is the TRACK that refuses to shrink, so
     * the item was already free to ellipsize and simply never given a smaller
     * box to do it in. The floor has to be lifted on the track itself.
     *
     * `minmax(0, max-content)` is the correct spelling and `minmax(0, auto)` is
     * NOT -- this was measured, not reasoned. With `auto` as the MAXIMUM the
     * track still refused to shrink: at 641px the tracks resolved to
     * 125.0px | 290.8px | 169.2px, the centre holding its full max-content
     * width while the two supposedly-equal `1fr` tracks were forced to
     * DIFFERENT sizes (125 vs 169) to absorb it -- which also silently destroys
     * the symmetry the equal side tracks exist to provide. `auto` used as a
     * minmax maximum resolves to max-content, so `minmax(0, auto)` states
     * exactly the floor the bare `auto` already had.
     *
     * With `max-content` the centre track keeps its content width whenever it
     * fits -- so wide viewports are byte-for-byte unchanged, and the equal
     * `1fr` side tracks still hold the name on the VIEWPORT centre (measured
     * drift 0px at 780-1920px) -- but it may now be squeezed below its content
     * when the side tracks need the room. The name then ellipsizes, which is
     * the documented intent of `min-width: 0` + `text-overflow: ellipsis`
     * directly below and was unreachable until now. */
    grid-template-columns: 1fr minmax(0, max-content) 1fr;
    align-items: center;
    /* Taller than the pre-inversion 64px to give the logo real breathing
     * room (see __logo below). Capped deliberately: Jake suggested the bar
     * could follow the branding image's height, but an uploaded logo is
     * arbitrary -- a 2198x514 or a portrait upload would produce an absurd
     * band. A fixed, generous height with a height-capped logo inside gives
     * the same "the mark has room" result and cannot be blown out by an
     * upload. */
    height: 76px;
    padding: 0 20px;
    gap: 8px;
    /* Positioning context for the viewport-centered store name below. */
    position: relative;
    /* The bar is white now, so the inherited `color: white` that the name and
     * the controls relied on would render them invisible. Each re-inks
     * explicitly below; this sets the default for anything added later, so a
     * future addition inherits a LEGIBLE ink rather than an invisible one. */
    color: var(--tf-fg-1, #1b2a38);
    /* A white bar against a white page needs a boundary of its own -- without
     * it the header and the content below merge into one undifferentiated
     * field. The category nav's brand color sits directly beneath, so this is
     * a hairline rather than a heavy rule. */
    border-bottom: 1px solid var(--tf-border, #e4e9ee);
    /* Topbar pins by being a non-shrinking flex item in a non-scrolling
     * parent. No sticky needed under the fixed-viewport pattern. */
    flex-shrink: 0;
}
.storefront-topbar__logo-link {
    /* The anchor is a pure wrapper -- it must add no box of its own, or it
     * would introduce a second source of spacing beside the image's padding.
     * `display: flex` collapses the inline-element baseline gap that would
     * otherwise appear under the img and make the bar's left slot taller than
     * the logo itself.
     *
     * flex-shrink: 0 mirrors the image's own rule: the store name is
     * absolutely positioned at the bar's centre and is NOT centred between
     * the side slots, so a slot that shrank would not move the name -- but it
     * WOULD squash the logo. Keeping the intrinsic width preserves both. */
    display: flex;
    align-items: center;
    flex-shrink: 0;
}
.storefront-topbar__logo {
    /* Height-capped so an arbitrary upload (portrait, huge, tiny) always fits
     * the topbar without distortion; width follows from the image's own
     * aspect ratio. The cap rose with the bar height (36px -> 44px) so the
     * mark reads at a deliberate size rather than as an afterthought.
     *
     * The padding is the fix for Jake's "it butts against the edges": T+L's
     * logo has ZERO white margin of its own (ink runs edge to edge, measured),
     * so with no padding its artwork touches the bar boundary and reads as a
     * crop. The bar supplies the margin the asset does not have. */
    height: 44px;
    max-width: 190px;
    width: auto;
    object-fit: contain;
    padding: 6px 10px;
    margin-right: 8px;
    /* THE LEFT-HAND HALF OF #3056.
     *
     * This was `flex-shrink: 0`, and that is what let the store name land on
     * the logo. Traced at 700px with a long name: the bar's `1fr` side track
     * resolved to 176.6px, but this image refused to go below its 190px cap
     * (plus 20px padding and 8px margin = 218px), so it OVERFLOWED the brand
     * slot rightward -- brand box [20, 238] inside a track ending at 196.6 --
     * while the name's centre cell began at x=204.6. The two boxes therefore
     * intersected by ~33px and the name painted over the mark.
     *
     * `min-width: 0` on the brand wrapper did not prevent this and could not:
     * the wrapper was already free to shrink, and did. It was this CHILD that
     * held the width, because a flex item with `flex-shrink: 0` is exempt from
     * the shrinking its container is doing.
     *
     * `box-sizing: border-box` is part of the fix, not decoration: with the
     * default content-box the 20px of horizontal padding is ADDED to the
     * resolved width, so a shrunk image would still overflow by its padding --
     * the same content-box trap the narrow-viewport rule below already
     * documents for `max-width`.
     *
     * The consequence at narrow widths is that a wide wordmark renders
     * SMALLER rather than being cropped or overlapped. `object-fit: contain`
     * and the height cap keep it undistorted, so it degrades the way an image
     * should. Above ~780px the track is wider than the cap and nothing here
     * binds, so every desktop width is unchanged. */
    box-sizing: border-box;
    flex-shrink: 1;
    min-width: 0;
    /* Bounded by the SLOT as well as by the absolute cap. `max-width: 190px`
     * alone is an absolute ceiling that knows nothing about how much room the
     * grid actually gave the left track; measured at 641px the track was
     * 125.0px while this image still drew 158.2px, because a replaced element
     * with a height cap and `object-fit: contain` derives its width from the
     * aspect ratio and does not shrink below that on flex pressure alone.
     * `100%` makes the parent's resolved width the binding constraint whenever
     * it is the smaller of the two. */
    max-width: min(190px, 100%);
}
.storefront-topbar__store-name {
    /* CENTERED IN THE VIEWPORT, as the middle cell of the bar's grid.
     *
     * WHY NOT `justify-content: center` (the trap the previous version of
     * this comment correctly warned about): in a FLEX row that centres the
     * name between the side slots, which equals the viewport centre only when
     * those slots are equally wide. They never are -- a wide logo on the left
     * against two controls (signed out) or six (signed in) on the right -- so
     * the name would visibly drift per store and per session.
     *
     * A GRID CENTRE CELL IS NOT THAT. The bar is `1fr auto 1fr`, and equal
     * `1fr` side tracks resolve to the SAME width no matter what each holds.
     * The centre track is therefore symmetric about the bar's midpoint, and
     * the bar spans the viewport, so this cell IS viewport-centred -- with no
     * absolute positioning and no arithmetic. Measured drift: 0px at 1280,
     * 1440 and 1920, signed in and signed out.
     *
     * WHY THE ABSOLUTE VERSION HAD TO GO. Out of flow, the name could not be
     * pushed aside by the controls, so it needed a max-width that RESERVED
     * them. Two attempts at that reserve shipped and both were wrong:
     * `44vw` (a fraction that has nothing to do with the controls' real
     * width), then `calc(100vw - 2*238px - ...)` in #3005, whose 238px was
     * measured on the SIGNED-OUT bar. A signed-in shopper's controls measure
     * 559-579px, so the name was handed ~341px it did not have and painted
     * itself under the Cart and Help controls at ordinary desktop widths --
     * Jake's report, reproduced at 203px of overlap with a long name.
     *
     * The deeper problem was that no constant COULD be right: the quantity
     * varies at runtime with session state, store name, logo and font
     * metrics. Measuring it live was tried and rejected -- see
     * docs/decisions/failed-approaches.md -- because reserving the real
     * 559px symmetrically needs 2*559 + 56 = 1174px of a 1150px bar, so the
     * name correctly computed a max-width of ZERO and disappeared. Trading a
     * collision for an invisible store name is not a fix.
     *
     * In flow as a grid cell the browser resolves this itself: the name gets
     * the space that is actually free, ellipsizes when there is not enough,
     * and slides off-centre only when centring is geometrically impossible
     * (a signed-in shopper below ~1200px). It cannot overlap the controls at
     * any width, because a grid cell cannot enter its sibling's track.
     *
     * min-width: 0 is load-bearing -- a grid item's default `min-width: auto`
     * refuses to shrink below its content's intrinsic width, which would push
     * the name INTO the controls' track and defeat the whole mechanism. It is
     * what makes the ellipsis below reachable. */
    min-width: 0;
    justify-self: center;
    /* NEVER WIDER THAN ITS OWN CELL.
     *
     * `min-width: 0` lets this box shrink; it does not stop it being LAID OUT
     * wider than the track and then painting over the neighbours. Measured at
     * 641px with a 398px name, the centre track was ~290px and the name drew
     * from x=45.8 -- straight across the logo. `overflow: hidden` above only
     * clips the TEXT to the box, and the box itself was the thing overflowing.
     *
     * `max-width: 100%` binds the box to the track, which is what finally makes
     * the ellipsis reachable: the text is clipped at the cell boundary and
     * ends in "..." instead of running under the mark. Above ~780px the track
     * is wider than the name and this does not bind, so wide viewports are
     * unchanged. */
    /* THE SIDE RESERVE -- and yes, this file has been burned by a reserve
     * TWICE, so read why this one is different before touching it.
     *
     * The mechanism first. A grid track sized `auto` (or `minmax(0,
     * max-content)`) takes its MAX-CONTENT width and the `1fr` side tracks
     * yield to it; measured at 641px the tracks resolved to 17.8px | 398px |
     * 169.2px, so a long name simply drew across the logo. The track follows
     * the ITEM's width exactly, which is why capping the item is what moves the
     * track -- `min-width: 0` and `max-width: 100%` both fail here, the latter
     * because a percentage resolves against the very grid area whose size it is
     * trying to constrain.
     *
     * WHY A CONSTANT IS DEFENSIBLE NOW AND WAS NOT BEFORE. #3005 reserved
     * 238px, measured on the SIGNED-OUT bar; a signed-in shopper's controls
     * really measured 559-579px, so the name was handed ~340px it did not have.
     * The reserve could not be right at any value because the right slot's
     * width varied hugely with session state. THAT IS THE FACT THIS CHANGE
     * ALTERED: collapsing four labelled controls into one 34px avatar makes the
     * right slot 189px signed-IN and 147px signed-OUT -- a 42px spread instead
     * of a 421px one. Measured, both states, in tools/topbar-geometry.
     *
     * 420px = 2 x 190px (the widest side slot, using the LARGER signed-in
     * number so the reserve is never short) + 40px of bar padding. Reserving
     * symmetrically is what keeps the name viewport-centred rather than merely
     * uncollided.
     *
     * AND IT CANNOT ERASE THE NAME, the other way a reserve has failed here: at
     * the 640px breakpoint the name moves to its own full-width row, so the
     * narrowest width this rule ever applies at is 641px, where it still yields
     * 221px of name. The `min()` floor is belt-and-braces for that -- a
     * reserve that computes to zero is how an earlier attempt traded a
     * collision for an invisible store name, which is not a fix.
     *
     * Verified across 390-1920px in BOTH session states, with and without a
     * logo, and with a name long enough to overflow: zero collisions, zero
     * centring drift. */
    max-width: max(120px, calc(100vw - 420px));
    /* Re-inked for the white bar -- this was `color: white`, which would be
     * invisible now. */
    color: var(--tf-fg-1, #1b2a38);
    font-size: 1.25rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    /* NOTE: the absolute version set `pointer-events: none` here so the
     * out-of-flow name could not swallow clicks aimed at the controls it sat
     * above. In the grid it occupies its own track and can never overlap
     * them, so that guard is not merely unnecessary -- keeping it would make
     * the store-name Home link permanently unclickable. */
}
.storefront-topbar__store-name--link {
    /* When the name is a Home link it still LOOKS like the store name -- no
     * button chrome, no underline, inherits the bar's ink -- just clickable.
     * Re-enables the pointer events the centering rule above disables. */
    text-decoration: none;
    cursor: pointer;
    pointer-events: auto;
}
.storefront-topbar__store-name--link:hover {
    text-decoration: underline;
}

.storefront-topbar__brand {
    /* LEFT grid track. Purely structural -- no padding, margin or background
     * of its own, so the logo sits exactly where it did as a direct child.
     * `justify-self: start` keeps the mark against the bar's leading edge
     * while the track itself stretches to balance its `1fr` twin on the
     * right, which is what holds the centre cell on the viewport centre. */
    display: flex;
    align-items: center;
    justify-self: start;
    min-width: 0;
    /* CLIPS TO ITS OWN TRACK. `min-width: 0` lets this box shrink, but a
     * shrunken box with `overflow: visible` still lets its child PAINT outside
     * it -- which is precisely how the logo reached the store name's cell at
     * 641px (brand box [20, 186.2] against a track ending at 145). Clipping
     * makes the track boundary real rather than advisory, so a stubborn child
     * can never again reach into the centre cell.
     *
     * Safe for the logo specifically because it is already bounded to
     * `min(190px, 100%)` above and so has nothing left to clip; this is the
     * belt to that braces, and it is what makes the guarantee structural
     * instead of contingent on the image's intrinsic size. */
    overflow: hidden;
}
.storefront-topbar__controls {
    /* RIGHT grid track. Groups the controls so the bar has exactly THREE
     * children and the three-column mapping is positional and unambiguous;
     * as loose children, adding a control silently shifted every track.
     *
     * Reproduces the 8px gap the controls had as direct children of the flex
     * bar, so their spacing is unchanged by the wrapper.
     *
     * flex-shrink: 0 with `justify-self: end` keeps the controls at their
     * intrinsic width against the trailing edge: the store name ellipsizes to
     * clear them, never the reverse. Shrinking the controls instead would
     * clip a hit target the shopper needs to check out. */
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    justify-self: end;
}
.storefront-topbar__btn {
    background: transparent;
    border: none;
    /* Re-inked for the white bar (was `color: white`). Slightly quieter than
     * the store name so the name stays the primary element in the bar. */
    color: var(--tf-fg-2, #3f4f5c);
    padding: 6px 12px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.storefront-topbar__btn:hover {
    color: var(--tf-fg-1, #1b2a38);
}
/*
 * HELP: a borderless icon button carrying a 26px glyph.
 *
 * It was a filled green pill matching the distributor topbar. Jake called the
 * rectangle distracting, and the fill was the larger half of that: the pill
 * carried the same green as the category band directly below it, so a control
 * meant to be quietly available read as a second navigation element shouting
 * inside an otherwise white bar. Rounding a green disc would have kept exactly
 * that property, so the fill went with the shape. That produced an OUTLINED
 * circle, which is what this rule described until 2026-08-29.
 *
 * THE OUTLINE IS NOW GONE TOO. Jake: "The circle around the help icon (button
 * size) is too big -- I think we can just have a button with no outline, but
 * I'd like the (?) glyph to be about 5px bigger to match the sillhouette menu."
 *
 * NOTE WHICH THING WAS TOO BIG, because the report and the fix are not the
 * same property and reading it literally would have produced the wrong change.
 * The "circle" is the BORDER -- the 34px box draws nothing on its own. So
 * "the circle is too big" is a judgement about the ring, and deleting the ring
 * answers it exactly. Shrinking the box would have shrunk the HIT TARGET, which
 * is not what he asked for and costs the shopper something he did not offer to
 * trade. The box therefore stays 34px (see below); the ring is what left.
 *
 * The end state matches the account trigger beside it: both are 34px,
 * borderless, transparent-at-rest, tinting only on hover. That pairing is the
 * point -- the two sat next to each other as a ringed circle and a bare
 * silhouette, which is the doubled-shape mismatch this removes.
 *
 * THE CONTRAST OBLIGATION MOVED RATHER THAN LAPSED, and this is the part worth
 * reading before touching any of it. With no fill AND no border, the glyph is
 * the whole visible control at rest and the FOCUS RING is the only boundary
 * this button ever draws. Both are pinned by
 * styles/__tests__/help-affordance-contrast.spec.js:
 *
 *   the GLYPH is text on the white bar       -> owes 4.5:1 (--tf-fg-2 is 8.6:1)
 *   the FOCUS RING is a non-text boundary    -> owes 3:1 (--tf-secondary)
 *
 * THE 1.45:1 TRAP THAT CAUGHT THE OUTLINED VERSION still applies, now to the
 * focus ring. Its first draft used --tf-border-strong, the intuitive token for
 * a hairline -- #d6d6d6, only 1.45:1 on this white bar, less than half the 3:1
 * floor. The border tokens are sized for dividers BETWEEN surfaces, not for the
 * edge of an interactive element. Do not reach for one here: the focus ring is
 * now the sole affordance a keyboard user has, so an invisible one leaves them
 * with nothing at all.
 *
 * SQUARE BOX + 50% RADIUS: `border-radius: 50%` on a non-square box produces an
 * oval. Width and height are therefore equal and explicit, and `padding: 0`
 * keeps them so -- the inherited __btn padding would otherwise widen the box
 * asymmetrically. The radius outlives the border because it still shapes the
 * hover tint and the focus ring.
 *
 * 34px SURVIVES THE RESTYLE, deliberately. It is the hit target, not the
 * circle: WCAG 2.2 target size (2.5.8) floors an interactive control at 24x24
 * CSS px, and this is a primary help affordance in a 76px bar that has room --
 * the account-menu collapse REMOVED four labelled controls from this row.
 * Shrinking a control to a dot is the cheap way to relieve a crowded bar at the
 * shopper's expense, and the bar is not crowded.
 *
 * This class no longer composes with `storefront-topbar__btn` in the template
 * (it used to), because that rule's inline-flex padding and gap were sized for
 * an icon-plus-label box and fought every dimension set here. Everything the
 * button needs is stated in this block, so the two cannot drift.
 */
.storefront-topbar__help {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    border-radius: 50%;
    background: transparent;
    /* NO BORDER. Jake, 2026-08-29: "The circle around the help icon (button
     * size) is too big -- I think we can just have a button with no outline."
     *
     * The distinction that decided this fix: the "circle" a viewer sees IS the
     * border. The 34px box is invisible on its own, so "the circle is too big"
     * is a statement about the drawn ring, and deleting the ring answers it
     * directly. Shrinking the box would answer a different question at the
     * shopper's expense -- see the width comment above.
     *
     * `border-radius` survives the border deliberately: it still shapes the
     * HOVER tint, which would otherwise land as a square behind a round glyph.
     */
    color: var(--tf-fg-2, #3f4f5c);
    cursor: pointer;
    flex-shrink: 0;
}
.storefront-topbar__help:hover {
    /* Fills only on hover -- a light tint, which is where a quiet control is
     * allowed to acknowledge the pointer without becoming the loudest thing in
     * the bar at rest. With the resting border gone this tint is now the only
     * POINTER feedback the control has, which is why it stays. */
    background: var(--tf-surface-2, #f5f7f9);
    color: var(--tf-fg-1, #1b2a38);
}
.storefront-topbar__help:focus-visible {
    /* An icon-only control with no focus ring is unreachable in practice for a
     * keyboard user: there is no label to read and nothing to see.
     *
     * THIS IS NOW THE ONLY BOUNDARY THE BUTTON EVER DRAWS. With the fill gone
     * (2026-08-28) and the resting border gone too (2026-08-29), there is no
     * always-on outline left to fall back on, so this rule is the whole of the
     * keyboard affordance rather than a reinforcement of a visible shape. It
     * is pinned by two tests -- one that it EXISTS, one that its token clears
     * 3:1 on the white bar -- because deleting it now costs more than it did
     * when it was merely the strongest of several cues. */
    outline: 2px solid var(--tf-secondary, #133752);
    outline-offset: 2px;
}
.storefront-topbar__help .tf-icon {
    /* 26px, matching the account silhouette's `size="26"` exactly.
     *
     * Jake asked for "about 5px bigger to match the sillhouette menu" -- and
     * the second clause settles what the first leaves open. 25px would also be
     * "about 5px bigger"; 26px is the size that actually MATCHES, which is the
     * stated goal. The two glyphs now read as a deliberate pair rather than as
     * two nearly-equal sizes.
     *
     * This also does the work the deleted border used to do. Without a ring,
     * the glyph is the entire visible control, so it has to carry the presence
     * the outline was providing -- a 20px mark in a borderless 34px box would
     * have read as smaller than before, not just quieter.
     */
    font-size: 26px;
    line-height: 1;
}

/*
 * ACCOUNT TRIGGER: the person-silhouette avatar that replaced four labelled
 * controls (Profile / Orders / Addresses / Logout).
 *
 * Sized and shaped to match the Help circle beside it so the two read as a
 * pair of icon affordances rather than two unrelated shapes. It carries NO
 * border, which is the one deliberate difference: the glyph is a filled
 * silhouette that already has its own visual mass, and ringing it as well
 * produced a circle-inside-a-circle -- the same doubled-shape problem that
 * made the old Help pill busy.
 *
 * THE WIDTH HERE IS THE POINT OF THE WHOLE CHANGE. The store name is the
 * centre cell of a `1fr auto 1fr` grid and gets whatever the side tracks
 * leave; four text controls claimed ~300px of the right track and squeezed it
 * until it collided with the logo (#3056). One 34px trigger does not.
 */
.storefront-topbar__account {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: var(--tf-fg-2, #3f4f5c);
    cursor: pointer;
    flex-shrink: 0;
}
.storefront-topbar__account:hover {
    background: var(--tf-surface-2, #f5f7f9);
    color: var(--tf-fg-1, #1b2a38);
}
.storefront-topbar__account:focus-visible {
    outline: 2px solid var(--tf-secondary, #133752);
    outline-offset: 2px;
}

/*
 * MENU ITEMS. Each is the real interactive element (router-link or button)
 * carrying the data-testid, stretched to fill its v-list-item so the whole row
 * is the hit target rather than just the few characters of the label -- a menu
 * row that only responds on its text is a control that looks bigger than it is.
 *
 * `background: none; border: none` normalises the Logout <button> to match the
 * three <a> elements: without it the browser's default button chrome would make
 * one row visibly different from its neighbours for no reason a shopper could
 * interpret.
 */
.storefront-account-menu__item {
    display: block;
    width: 100%;
    padding: 2px 0;
    background: none;
    border: none;
    font: inherit;
    text-align: left;
    text-decoration: none;
    color: var(--tf-fg-1, #1b2a38);
    cursor: pointer;
}
.storefront-account-menu__item:hover {
    text-decoration: underline;
}

.storefront-badge {
    /* Darkened from #22c55e as part of the white-bar re-inking.
     *
     * The pill carries the cart COUNT -- a number the shopper reads, so it
     * is text and owes 4.5:1. The old green gave its white text only
     * 2.28:1, which was already under the bar; the inversion then made it
     * worse in a second way, because a pale green pill on a WHITE bar also
     * failed the 3:1 non-text floor against its own surround (2.28:1), so
     * the pill's own boundary washed out. This green clears both bars at
     * 5.02:1 while still reading as green. */
    background: #15803d;
    color: white;
    border-radius: 10px;
    padding: 0 6px;
    font-size: 0.75rem;
    min-width: 18px;
    text-align: center;
}
.storefront-main {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    /* The category nav is `position: sticky; top: 0` inside this scroller
     * (StorefrontCategoryNav), so anything scrolled into view programmatically
     * or by keyboard focus would land UNDER it. This clearance keeps focused
     * fields and scrollIntoView targets below the pinned bar. rem, not px, so
     * it tracks the bar's font-driven height; the bar is ~3.3rem tall. */
    scroll-padding-top: 4rem;
}

/*
 * NARROW VIEWPORTS: the store name becomes its own row.
 *
 * THE BUG (Jake, 2026-08-26): on a phone the absolutely-positioned name ran
 * straight into the Cart control -- measured at 390px, the name's box ended at
 * x=271 while Cart began at x=254, a 17px overlap with the word "Cart" sitting
 * on top of the store name. With no logo and a longer name it overlapped BOTH
 * Cart and Help. `max-width: 44vw` was the intended collision guard and is not
 * enough: an out-of-flow element cannot be reflowed away by the browser, so
 * shrinking it only ellipsizes the name harder while it still overlaps.
 *
 * WHY A SECOND ROW RATHER THAN HIDING THE NAME. Jake offered either. The name
 * is the store's identity and the logo is OPTIONAL -- `brandLogoUrl` is null
 * for a store with no upload and the <img> is then absent from the DOM
 * entirely, while `storeName` always renders (it falls back to "Shop"). So
 * `display: none` here would leave a logo-less store with NO visible identity
 * on a phone, which is the one case that most needs it. Giving the name its
 * own row keeps identity at every width.
 *
 * WHY 640px. This is the first breakpoint in this file, so it is chosen rather
 * than inherited. The constraint is the row's own content, not a device:
 * measured, the controls need 238px (Cart 85 + Help 145 + an 8px gap) and the
 * logo slot up to 218px (190px cap + padding + margin), which leaves nothing
 * for a centred name much below ~680px. 640px sits above every phone in
 * portrait and below a tablet in landscape, so the stacked layout lands on
 * phones while the single row survives everywhere it still fits.
 *
 * Between 640px and ~680px the single row is retained and the name simply
 * ellipsizes earlier -- that is the max-width calc's job, not this
 * breakpoint's. The two mechanisms are deliberately separate: the calc keeps
 * the name off the controls at EVERY width (including desktop, where a long
 * name previously overlapped Cart -- see that rule), and this breakpoint
 * handles the narrower case where even an ellipsized name leaves the row too
 * cramped to read.
 *
 * THIS BLOCK MUST STAY LAST IN THE SHEET. A media query adds no specificity,
 * so these rules beat the base ones on SOURCE ORDER alone. Placed earlier
 * (where it originally was, right after the store-name rules) the narrow
 * `padding` overrides below were silently inert -- the media query matched,
 * the rule was in the file, and the computed padding was still the base value.
 * Anything added after this block that touches the same properties will
 * re-break it the same way.
 *
 * WHAT IS DELIBERATELY NOT CHANGED: above this breakpoint the name stays
 * absolutely positioned and viewport-centred. The comment on
 * .storefront-topbar__store-name explains why in-flow centring is wrong
 * (`justify-content: center` centres between the side slots, which equals the
 * viewport centre only when they are equally wide -- they never are, so the
 * name would visibly drift per store and per session). That reasoning is
 * untouched; this only applies where the single row cannot fit at all, and
 * there the name is full-width, so "centred between the slots" and "centred in
 * the viewport" are the same position and no drift is possible.
 */
@media (max-width: 640px) {
    .storefront-topbar {
        /* Two rows: [logo | controls] then [store name] full-width.
         *
         * Now that the bar is a grid, this is DECLARED rather than coaxed out
         * of `flex-wrap`. The previous version wrapped on overflow, which made
         * the row count a CONSEQUENCE of the content: at 390px a max-width
         * logo plus Cart plus Help overflowed, so the CONTROLS wrapped too and
         * the bar became three rows / 136px, with the height varying by
         * whether the store had a logo. An explicit two-row template cannot do
         * that -- the name is placed on row 2 by the grid, not by running out
         * of space -- so the bar is exactly two rows at a stable height with or
         * without a logo. The logo cap below still keeps row 1 intact.
         *
         * `auto auto` columns (not the desktop `1fr auto 1fr`) let the logo and
         * controls take their intrinsic widths and sit at the row's two ends;
         * the name spans both columns beneath them, so this row's centre is
         * the viewport centre by construction and no drift is possible. */
        grid-template-columns: minmax(0, auto) minmax(0, 1fr);
        grid-template-rows: auto auto;
        justify-content: space-between;
        /* The fixed 76px was sized for ONE row of a 44px-capped logo. A second
         * row no longer fits inside it, and a fixed height would clip the
         * name. min-height keeps the single-row appearance identical for a
         * short bar while letting the second row add its own height. */
        height: auto;
        min-height: 76px;
        /* The name row would otherwise sit tight against the category nav
         * directly below. */
        padding-bottom: 8px;
    }
    .storefront-topbar__logo {
        /* Narrower cap so the logo cannot push Cart and Help onto a row of
         * their own (see flex-wrap above). Measured at 390px: the row has
         * 350px of usable width after the 20px side padding, and Cart (85px) +
         * Help (145px) + two 8px gaps already claim 246px. That leaves ~104px
         * for the logo, so the cap is 90px with margin to spare.
         *
         * border-box is load-bearing, not decoration: the base rule sets
         * `padding: 6px 10px` and the default content-box sizing ADDS it to
         * max-width, so a 110px cap measured 122px on the page and the row
         * still overflowed. Sizing the padding INSIDE the cap makes the
         * declared number the number the layout actually gets.
         *
         * The height cap is untouched, so a wordmark keeps its aspect ratio
         * and simply renders smaller -- object-fit: contain already guarantees
         * no distortion. */
        box-sizing: border-box;
        /* 70px, not 90: measured at 360px (the narrowest common phone), the
         * row has 320px usable and the trimmed controls still claim ~226px
         * including gaps, so the logo slot has ~94px to work with and 90 + its
         * own margin tipped Help onto a third row. 70px holds two rows all the
         * way down to 320px. The height cap and object-fit: contain keep the
         * mark undistorted -- it simply renders smaller on a small screen,
         * which is the expected trade. */
        max-width: 70px;
        /* The 6px 10px padding was sized to give a 190px mark breathing room
         * against the bar edges; at 90px it would be a large share of the box,
         * so trim it rather than let it eat the artwork. */
        padding: 4px 6px;
        margin-right: 4px;
    }
    .storefront-topbar__btn {
        /* Trim the horizontal padding so the control row survives the
         * narrowest phones. At 360px the row has 320px of usable width while
         * logo (94) + Cart (85) + Help (145) + gaps needed ~340, so Help
         * wrapped to a third row and the bar grew to 124px. Reclaiming 6px per
         * control per side brings the row back inside the budget and holds the
         * bar at two rows for every fixture measured down to 320px.
         *
         * Only the horizontal padding moves -- the vertical padding sets the
         * controls' hit-target height, which should not shrink on the devices
         * where taps are least precise. */
        padding: 6px;
    }
    /* NOTE: no narrow-viewport override for `.storefront-topbar__help` any
     * more, and its absence is deliberate rather than an omission. The rule
     * that stood here reclaimed horizontal padding from the Help PILL, which
     * was the widest control in the row. Help is now a fixed 34px circle with
     * `padding: 0` -- restating a padding here would stop it being a circle at
     * exactly the widths where the bar is most cramped, which is the opposite
     * of what the old rule was for. The circle is already narrower than the
     * padding-trimmed pill ever was. */
    .storefront-topbar__controls {
        /* WRAP ON A PHONE -- kept, though it now has much less work to do.
         *
         * It was written when a SIGNED-IN shopper carried six controls (Cart,
         * Help, Profile, Orders, Addresses, Logout) measuring ~491px, which
         * cannot fit a 320px row at any padding. That set is now THREE (Cart,
         * a 34px Help circle, a 34px account avatar), so the row fits on one
         * line on every phone measured. The rule stays because it is the
         * behaviour that keeps a trailing control reachable rather than
         * off-screen if a future control joins the row or a translated Cart
         * label runs long -- it costs nothing when the row already fits.
         *
         * `justify-content: flex-end` keeps the block against the trailing
         * edge as it wraps, matching the single-row case. The bar's
         * `min-height` (not a fixed height) absorbs any extra line. */
        flex-wrap: wrap;
        justify-content: flex-end;
    }
    .storefront-topbar__store-name {
        /* SECOND ROW, spanning both columns. Placed explicitly rather than
         * relying on flex `order` + a 100% flex-basis: the grid puts the name
         * on row 2 by declaration, so it renders BELOW the logo/controls row
         * and ABOVE the category nav -- the placement Jake asked for -- and it
         * cannot be shuffled by a sibling appearing or disappearing.
         *
         * The name stays SECOND IN THE DOM (right after the brand slot), so
         * keyboard tab position and reading order are unchanged; only the
         * visual row moves. That is the same deliberate, small
         * visual-vs-DOM-order trade the flex `order` made, and it is safe for
         * the same reason: the bar is a flat list of independent landmarks
         * with no sequence a reader depends on between "the store name" and
         * "the cart button".
         *
         * Spanning the full width makes this row's centre the viewport centre
         * by construction, so `justify-self: center` from the desktop rule
         * needs no override -- there is nothing here for it to drift against. */
        grid-column: 1 / -1;
        grid-row: 2;
        /* min-width: 0 again, for the same reason as the desktop rule and it
         * is NOT inherited: a grid item defaults to `min-width: auto`, which
         * refuses to shrink below the text's intrinsic width. Without it a
         * long name measured 398px inside a 320px bar and ran off the edge
         * instead of ellipsizing -- `max-width: 100%` alone does not bind,
         * because the floor wins over the ceiling. */
        min-width: 0;
        max-width: 100%;
        text-align: center;
    }
}
</style>
